import { type NextApiRequest, type NextApiResponse } from 'next';
import { env } from 'next-runtime-env';
import { v4 as uuidv4 } from 'uuid';
import { stringifyRequestBody } from '@/services/backend/BackendBaseService';
import { ErrorCategory, ResponseError } from './exceptions';
import { logger } from './logger';

/**
 * W3C Trace Context / OTel headers from the browser. Browser RUM injects these on same-origin
 * `/api/*` requests when `allowedTracingUrls` is configured; we forward them to microservices so
 * backend traces can link to the RUM session.
 */
const TRACE_PROPAGATION_HEADER_NAMES = ['traceparent', 'tracestate', 'baggage'] as const;

function mergeTracePropagationFromClient(incoming: Headers | undefined, base: HeadersInit): HeadersInit {
  if (!incoming) {
    return base;
  }
  const merged: Record<string, string> = {
    ...(base as Record<string, string>),
  };
  for (const name of TRACE_PROPAGATION_HEADER_NAMES) {
    const value = incoming.get(name);
    if (value) {
      merged[name] = value;
    }
  }
  return merged;
}

export const genericApiErrorMessage =
  "Something went wrong and this action couldn't be completed. Please try refreshing the page. If this problem persists, please email at aga_support@atlas-sp.com";

export type HttpRequestInput = {
  api?: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  headers?: Headers;
  body?: string | any; // Can be string or object; Next.js parses JSON into objects
};

export type HttpRequest = {
  method: string;
  headers: HeadersInit;
  body?: string;
  duplex?: 'half';
};

const getBaseApiUrl = (api: string): string => {
  switch (api) {
    case 'funding':
      return env('AGA_SERVICE_FUNDING_URL') || '';
    case 'authorization':
      return env('AGA_SERVICE_AUTHORIZATION_URL') || '';
    case 'measurement':
      return env('AGA_SERVICE_MEASUREMENT_URL') || '';
    case 'reporting':
      return env('AGA_SERVICE_REPORTING_URL') || '';
    case 'deals':
      return env('AGA_SERVICE_DEAL_URL') || '';
    case 'collateral':
      return env('AGA_SERVICE_COLLATERAL_URL') || '';
    case 'notification':
      return env('AGA_SERVICE_NOTIFICATION_URL') || '';
    case 'digitalconnect':
      return `${env('AGA_SERVICE_DIGITALCONNECT_URL') || ''}api/`;
    default:
      return '';
  }
};

const http = async (
  baseUrl: string,
  path: string,
  init: HttpRequest,
  api: string,
  correlationId: string,
): Promise<Response> => {
  const requestPath = `${baseUrl}${path}`;
  const request = new Request(requestPath, init);

  try {
    const data = await fetch(request).then(async res => {
      const contentType = res.headers.get('content-type');

      if (!res.ok) {
        if (
          contentType &&
          (contentType.includes('application/json') || contentType.includes('application/problem+json'))
        ) {
          const responseData = await res.json();
          throw new ResponseError(
            responseData.message || responseData.title || 'Something went wrong',
            res.status || 500,
            {
              cause: responseData,
              category:
                res.status === 401
                  ? ErrorCategory.AUTHENTICATION
                  : res.status === 403
                    ? ErrorCategory.AUTHORIZATION
                    : ErrorCategory.INTERNAL,
            },
          );
        }

        throw new ResponseError('Something went wrong', res.status, { category: ErrorCategory.INTERNAL });
      }
      // Keep the Response so callers can copy headers such as Cache-Control.
      // Parsing here drops them, and the browser then reuses a stale GET.
      return res;
    });

    return data;
  } catch (e: any) {
    logger({ service: api, level: 'ERROR', body: { url: requestPath, error: e, correlationId } });
    throw e;
  }
};

const getJwt = async (request: NextApiRequest): Promise<string> => {
  const authHeader = request.headers.authorization;

  if (authHeader?.startsWith('Bearer ')) {
    const accessToken = authHeader.substring(7);
    return accessToken;
  }
  throw new ResponseError('User not signed in', 401, { category: ErrorCategory.AUTHENTICATION });
};

const getAuthHeaders = async (request: NextApiRequest, correlationId: string): Promise<HeadersInit> => {
  const token = await getJwt(request);
  return {
    Authorization: 'Bearer ' + token,
    'x-correlation-id': correlationId,
  };
};

/**
 * httpRequest - INTERNAL backend API communication function
 *
 * ⚠️  INTERNAL USE ONLY - Not exported to prevent direct imports
 *
 * This function handles authenticated requests to backend microservices.
 * It automatically reads authentication from middleware-set headers.
 *
 * AUTHENTICATION SOURCES (automatic):
 * 1. NextRequest object (from API routes): Middleware sets Authorization header
 * 2. Headers object (when passed): Contains Authorization header from middleware
 *
 * CALLED BY:
 * ✅ API Route handlers via handleRequest()
 * ✅ Internal route handler functions only
 *
 * NOT ACCESSIBLE TO:
 * ❌ Frontend components (not exported)
 * ❌ Server components (use BaseService instead)
 * ❌ Direct imports (function is not exported)
 *
 * @param api - Target microservice ('authorization', 'funding', etc.)
 * @param method - HTTP method
 * @param path - API endpoint path
 * @param headers - Headers object (contains Authorization header from middleware)
 * @param body - Request body
 * @param request - NextRequest object (for API routes)
 * @returns Promise<U> - Typed response from backend API
 * @throws ResponseError - If authentication fails or API error occurs
 */
const httpRequest = async <U>({
  api = 'funding',
  method,
  path,
  headers,
  body,
  request,
}: HttpRequestInput & { request: NextApiRequest }): Promise<U> => {
  const correlationId = uuidv4();
  const logPath = request.url || 'server-component';

  // 🔐 AUTHENTICATION HANDLING (Automatic via Middleware)
  // Middleware automatically sets Authorization header on all requests
  let authHeaders: HeadersInit;
  try {
    authHeaders = await getAuthHeaders(request, correlationId);
  } catch (authError) {
    console.error(`❌ [${logPath}] [AUTH-FAIL] Authentication failed before backend fetch:`, {
      error: authError instanceof Error ? authError.message : authError,
      correlationId,
    });
    throw authError;
  }

  const version = headers?.get('x-entity-version');
  const contentType = headers?.get('Content-Type') ?? 'application/json';
  const ssrm = headers?.get('ssrm-request');

  let httpHeaders: HeadersInit = {
    ...authHeaders,
    'Content-Type': contentType,
  };

  if (ssrm) {
    httpHeaders = { ...httpHeaders, 'ssrm-request': ssrm };
  }

  if (version) {
    httpHeaders = { ...httpHeaders, 'x-entity-version': version };
  }

  httpHeaders = mergeTracePropagationFromClient(headers, httpHeaders);

  let init: HttpRequest = { method, headers: httpHeaders, duplex: 'half' };

  // Stringify body if it's an object (Next.js API routes parse JSON into objects)
  init.body = stringifyRequestBody(body, method);

  const baseUrl = getBaseApiUrl(api);

  logger({ service: api, level: 'INFO', body: { url: `${baseUrl}${path}`, method, correlationId } });

  try {
    const result = await http(baseUrl, path, init, api, correlationId);
    return result as U;
  } catch (error) {
    console.error(`❌ [${logPath}] [POST-FETCH] Microservice request failed:`, {
      api,
      path,
      error: error instanceof Error ? error.message : 'Unknown error',
      correlationId,
    });
    throw error;
  }
};

export type HandleRequestOptions = {
  /** When set, replaces default `/api/{service}/…` path derivation (used by TrustReceipts and similar proxies). */
  resolveBackendPath?: (request: NextApiRequest) => string;
};

export const handleRequest = async (
  request: NextApiRequest,
  response: NextApiResponse,
  api: string = 'funding',
  options?: HandleRequestOptions,
) => {
  // Lazy server-only import — avoids pulling `fs` into the client bundle.
  // Only runs when MOCK_SERVICES=true; noop otherwise.
  if (typeof window === 'undefined' && process.env.MOCK_SERVICES === 'true') {
    const { handleMockRequest } = await import('./mockServiceInterceptor');
    if (handleMockRequest(request, response, api)) return;
  }

  const method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = request.method as
    | 'GET'
    | 'POST'
    | 'PUT'
    | 'DELETE'
    | 'PATCH';
  const correlationId = uuidv4();
  const requestUrl = request.url || '';

  try {
    const urlParts = requestUrl.split('?')[0].split('/');
    const endpoint = urlParts.slice(3) || [];
    const path = options?.resolveBackendPath ? options.resolveBackendPath(request) : endpoint.join('/');

    // headers - Convert from NextApiRequest headers to Headers object
    const headers = new Headers();
    if (request.headers) {
      Object.entries(request.headers).forEach(([key, value]) => {
        if (value) {
          headers.append(key, Array.isArray(value) ? value.join(', ') : value);
        }
      });
    }

    // Build query string from request.query, excluding the endpoint
    const queryParams = new URLSearchParams();
    if (request.query) {
      Object.entries(request.query).forEach(([key, value]) => {
        if (value && key !== 'endpoint') {
          if (Array.isArray(value)) {
            value.forEach(v => {
              queryParams.append(key, v);
            });
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }
    const paramString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    // Only include body for non-GET requests
    let body: any = undefined;
    if (method !== 'GET' && request?.body) {
      // Next.js automatically parses JSON body, so we need to stringify it again
      body = typeof request.body === 'string' ? request.body : JSON.stringify(request.body);
    }

    const data = await httpRequest<Response | JSON>({
      api,
      path: `${path}${paramString}`,
      method: method || 'GET',
      body,
      headers,
      request,
    });

    if (data instanceof Response) {
      const contentType = data.headers.get('content-type');

      if (!data.ok) {
        console.error('❌ Microservice returned error:', {
          status: data.status,
          contentType,
          correlationId,
        });
        if (
          contentType &&
          (contentType.includes('application/json') || contentType.includes('application/problem+json'))
        ) {
          const responseData = await data.json();
          console.error('❌ Error response data:', {
            data: responseData,
            correlationId,
          });
          throw new ResponseError(
            responseData.message || responseData.title || 'Something went wrong',
            data.status || 500,
            {
              cause: responseData,
              category:
                data.status === 401
                  ? ErrorCategory.AUTHENTICATION
                  : data.status === 403
                    ? ErrorCategory.AUTHORIZATION
                    : ErrorCategory.INTERNAL,
            },
          );
        }

        throw new ResponseError('Something went wrong', data.status, { category: ErrorCategory.INTERNAL });
      }
      if (data.status === 204) {
        response.status(data.status).end();
        return;
      }

      // Handle binary/non-JSON responses (like file downloads)
      if (!contentType || !contentType.includes('application/json')) {
        // Forward all headers from the backend
        data.headers.forEach((value, key) => {
          response.setHeader(key, value);
        });

        // Stream the binary data
        const buffer = await data.arrayBuffer();
        response.status(data.status).send(Buffer.from(buffer));
        return;
      }

      const cacheControl = data.headers.get('cache-control');
      if (cacheControl) {
        response.setHeader('Cache-Control', cacheControl);
      }
      response.status(data.status).json(await data.json());
      return;
    }

    response.status(200).json(data);
  } catch (e) {
    console.error(`❌ [${requestUrl}] Request handling failed:`, {
      error: e instanceof Error ? e.message : 'Unknown error',
      stack: e instanceof Error ? e.stack : undefined,
      correlationId,
    });

    logger({ service: api, level: 'ERROR', body: { error: e } });

    if (e instanceof ResponseError) {
      response.status(e.statusCode).json({ message: e.message, cause: e.cause });
    } else {
      response.status(500).json({ message: 'Something went wrong' });
    }
  }
};
